import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Users, 
  Globe2, 
  Brain, 
  Clock, 
  Award,
  Building2,
  Sparkles,
  Bot,
  Rocket,
  HeartHandshake
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const About = () => {
  const { t } = useTranslation();

  const values = [
    {
      icon: Brain,
      title: t('about.values.innovation.title'),
      description: t('about.values.innovation.description')
    },
    {
      icon: HeartHandshake,
      title: t('about.values.trust.title'),
      description: t('about.values.trust.description')
    },
    {
      icon: Rocket,
      title: t('about.values.excellence.title'),
      description: t('about.values.excellence.description')
    },
    {
      icon: Bot,
      title: t('about.values.technology.title'),
      description: t('about.values.technology.description')
    }
  ];

  const milestones = [
    {
      year: '2006',
      title: t('about.milestones.items.2006.title'),
      description: t('about.milestones.items.2006.description')
    },
    {
      year: '2010',
      title: t('about.milestones.items.2010.title'),
      description: t('about.milestones.items.2010.description')
    },
    {
      year: '2015',
      title: t('about.milestones.items.2015.title'),
      description: t('about.milestones.items.2015.description')
    },
    {
      year: '2020',
      title: t('about.milestones.items.2020.title'),
      description: t('about.milestones.items.2020.description')
    },
    {
      year: '2023',
      title: t('about.milestones.items.2023.title'),
      description: t('about.milestones.items.2023.description')
    }
  ];

  const stats = [
    { number: '2006', label: t('about.stats.founded'), icon: Building2 },
    { number: '3', label: t('about.stats.locations'), icon: Globe2 },
    { number: '24/7', label: t('about.stats.service'), icon: Clock },
    { number: '500+', label: t('about.stats.clients'), icon: Users }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20
      }
    }
  };

  return (
    <>
      <SEO 
        title={t('common.company')}
        description={t('about.description')}
        keywords="vefsíða, fyrirtæki, saga, teymi, starfsfólk, reynsla"
      />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <motion.div 
              className="text-center max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                className="inline-flex items-center px-4 py-2 bg-[#EA295A]/5 rounded-full mb-6"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Sparkles className="w-4 h-4 text-[#EA295A] mr-2" />
                <span className="text-[#EA295A] text-sm font-medium">2006</span>
              </motion.div>
              <h1 className="text-4xl font-bold text-gray-900 mb-6">
                {t('about.title')}
              </h1>
              <p className="text-xl text-gray-600">
                {t('about.description')}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Stats Section */}
        <motion.div 
          className="py-16 bg-white"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div 
                  key={index}
                  className="text-center"
                  variants={itemVariants}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-4">
                    <stat.icon className="w-6 h-6 text-[#EA295A]" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* About Section */}
        <motion.div 
          className="py-24 bg-gray-50"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div variants={itemVariants}>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  {t('about.history.title')}
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  {t('about.history.description')}
                </p>
                <p className="text-lg text-gray-600">
                  {t('about.history.current')}
                </p>
              </motion.div>
              <motion.div variants={itemVariants} className="relative">
                <img 
                  src="https://res.cloudinary.com/djimcqajg/image/upload/v1741454400/vs-office_ah094w.jpg" 
                  alt="Team collaboration" 
                  className="rounded-2xl shadow-xl"
                />
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#EA295A]/10 to-transparent" />
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Values Section */}
        <motion.div 
          className="py-24 bg-white"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="text-center max-w-3xl mx-auto mb-16"
              variants={itemVariants}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t('about.values.title')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('about.values.description')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-gray-50 rounded-xl p-8 text-center"
                  whileHover={{ y: -5 }}
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-[#EA295A]/10 mb-6">
                    <value.icon className="w-8 h-8 text-[#EA295A]" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Timeline Section */}
        <motion.div 
          className="py-24 bg-gray-50"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="text-center max-w-3xl mx-auto mb-16"
              variants={itemVariants}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t('about.milestones.title')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('about.milestones.description')}
              </p>
            </motion.div>

            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-px h-full w-0.5 bg-gray-200" />
              <div className="space-y-12">
                {milestones.map((milestone, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className={`relative flex items-center ${
                      index % 2 === 0 ? 'justify-start' : 'justify-end'
                    }`}
                  >
                    <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8' : 'pl-8'}`}>
                      <div className="bg-white rounded-xl p-6 shadow-sm">
                        <div className="text-[#EA295A] font-bold mb-2">{milestone.year}</div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{milestone.title}</h3>
                        <p className="text-gray-600">{milestone.description}</p>
                      </div>
                    </div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-[#EA295A]" />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          className="py-24 bg-white"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
              <motion.h2 
                variants={itemVariants}
                className="text-3xl font-bold text-white mb-6"
              >
                {t('about.cta.title')}
              </motion.h2>
              <motion.p 
                variants={itemVariants}
                className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
              >
                {t('about.cta.description')}
              </motion.p>
              <motion.div variants={itemVariants}>
                <Link to="/fa-tilbod">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                  >
                    {t('about.cta.button')}
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default About;